import * as React from 'react';

import { useMediaListener, BREAKPOINTS } from '@xing-com/util';

import { CONTENT_SWITCHER_BUTTON_LIST_TEST_ID } from '../constants';
import { ContentSwitcherButton } from '../content-switcher-button';
import { ContentSwitcherMore } from '../content-switcher-more';
import { useHandleContentSwitcherOverflow } from '../hooks/use-handle-content-switcher-overflow';
import { useKeyboardNavigation } from '../hooks/use-keyboard-navigation';

import * as Styled from './content-switcher.styles';
import type { ContentSwitcherProps } from './content-switcher.types';

export const ContentSwitcher = React.forwardRef<
  HTMLElement,
  ContentSwitcherProps
>(
  (
    {
      activeItemIndex = -1,
      allowDropdown = true,
      className,
      data = [],
      isFixed = false,
      moreButtonProps,
      role = 'tablist',
      size = 'medium',
      sizeConfined,
      sizeWide,
      variant,
      ...props
    },
    forwardedRef
  ) => {
    const ref = React.useRef<HTMLUListElement>(null);
    const isMobile = BREAKPOINTS.handheld === useMediaListener();
    const { visibleButtons, overflowedButtons } =
      useHandleContentSwitcherOverflow({
        allowDropdown: allowDropdown && !isMobile,
        data,
        containerRef: ref,
        size,
      });

    const { handleKeyDown } = useKeyboardNavigation();

    return (
      <Styled.ContentSwitcher
        $size={size}
        $sizeConfined={sizeConfined}
        $sizeWide={sizeWide}
        className={className}
        data-xds="ContentSwitcher"
        ref={forwardedRef}
        onKeyDown={handleKeyDown}
        {...props}
      >
        <Styled.ButtonList
          $noDropdown={!allowDropdown}
          $size={size}
          $sizeConfined={sizeConfined}
          $sizeWide={sizeWide}
          aria-orientation="horizontal"
          data-testid={CONTENT_SWITCHER_BUTTON_LIST_TEST_ID}
          ref={ref}
          role={role}
        >
          {visibleButtons.map((index) => (
            <Styled.ButtonListItem
              data-qa={`menu-item-${index}`}
              data-testid={`menu-item-${index}`}
              key={index}
              role="none"
            >
              <ContentSwitcherButton
                activeItemIndex={activeItemIndex}
                role={role === 'tablist' ? 'tab' : 'link'}
                selected={index === activeItemIndex}
                size={size}
                sizeConfined={sizeConfined}
                sizeWide={sizeWide}
                variant={variant}
                {...data[index]}
              >
                {data[index].copy}
              </ContentSwitcherButton>
            </Styled.ButtonListItem>
          ))}
        </Styled.ButtonList>
        {overflowedButtons.length > 0 && (
          <ContentSwitcherMore
            activeItemIndex={overflowedButtons.indexOf(activeItemIndex)}
            data={overflowedButtons.map((buttonIndex) => data[buttonIndex])}
            isFixed={isFixed}
            role={role === 'tablist' ? 'tab' : 'button'}
            selected={activeItemIndex >= overflowedButtons[0]}
            size={size}
            sizeConfined={sizeConfined}
            sizeWide={sizeWide}
            variant={variant}
            {...moreButtonProps}
          />
        )}
      </Styled.ContentSwitcher>
    );
  }
);

ContentSwitcher.displayName = 'ContentSwitcher';
